import isEmpty from 'lodash-es/isEmpty';

/**
 * Function used to return value from local storage.
 * @param key
 * @return {any}
 */
export function getLocalStorage(key: string) {
  let value;

  try {
    value = localStorage.getItem(key);
  } catch (err) {
    console.error(`local storage is not supported; ${err}`);
    return false;
  }

  if (isStringJSON(value)) {
    return JSON.parse(value);
  }

  return value;
}
/**
 * Function validates if string is a JSON object.
 * @param  {string} value - Test string
 * @return {boolean}
 */
export function isStringJSON(value: string) {
  try {
    JSON.parse(value);
  } catch (e) {
    return false;
  }
  return true;
}
/**
 * Function used to set the key/value pair to the local storage
 * @param key
 * @param value
 * @return {boolean}
 */
export function setLocalStorage(key: string, value: any) {
  let valueStringified = value;

  if (!isEmpty(value)) {
    if (typeof value === 'object') {
      valueStringified = JSON.stringify(value);
    }
  }

  try {
    localStorage.setItem(key, valueStringified);
    return true;
  } catch (err) {
    console.error(`local storage is not supported; ${err}`);
    return false;
  }
}
/**
 * Function used to delete key/value pair from local storage
 * @param key
 * @return {boolean}
 */
export function localStorageRemoveItem(key: string) {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (err) {
    console.error(`local storage is not supported; ${err}`);
    return false;
  }
}
/**
 * Function used to get a cookie based on the provided cookie name.
 * If the cookie does not exist, returns null.
 * Otherwise, returns the contents of the cookie.
 * @param name
 * @return {string | null}
 */
export function getCookie(name) {
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    if (cookie.startsWith(name + '=')) {
      return cookie.substring(name.length + 1);
    }
  }
  return null;
}

/**
 * Function used to create a cookie.
 * Specify a number for @days for a persistent cookie.
 * The value can either be a string or an object.
 * @param key
 * @param value
 * @param days
 * @param domain
 */
export function createCookie(key: string, value: any, days: number, domain: string = '') {
  try {
    let expires, setValue, finalDomain;

    if (days) {
      const date = new Date();
      date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
      expires = `; expires=${date.toUTCString()}`;
    } else {
      expires = '';
    }
    if (domain !== '') {
      finalDomain = `;domain=${domain}`;
    } else {
      finalDomain = '';
    }

    setValue = value instanceof Object ? `'${JSON.stringify(value)}'` : value;
    document.cookie = `${key}=${setValue}${expires}${finalDomain};path=/`;
  } catch (err) {
    console.error('Cookies have been disabled.');
  }
}
/**
 * Function used to delete a cookie.
 * @param name
 */
export function removeCookie(key: string) {
  createCookie(key, '', -1);
}
/**
 * Function that handles local storage & cookie data retrieval.
 * Local storage data takes priority over cookie data.
 * @param key
 * @return {any}
 */
export function localStorageGetter(key: string) {
  return getLocalStorage(key) || getCookie(key) || null;
}
/**
 * Function that handles local storage & cookie data creation.
 * Local storage data takes priority over cookie data.
 * @param key
 * @param value
 */
export function localStorageSetter(key: string, value: any) {
  setLocalStorage(key, value) || createCookie(key, value, 99999);
}
/**
 * Function that handles local storage & cookie data deletion.
 * @param key
 */
export function localStorageRemover(key: string) {
  localStorageRemoveItem(key) || removeCookie(key);
}

export function getQueryParamByName(name, url = window.location.href) {
  name = name.replace(/[\[\]]/g, '\\$&');
  const regex = new RegExp('[?&]' + name + '(=([^&#]*)|&|#|$)'),
    results = regex.exec(url);
  if (!results) return null;
  if (!results[2]) return '';
  return decodeURIComponent(results[2].replace(/\+/g, ' '));
}
